import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ClearOutlined,
  CloudSyncOutlined,
  DatabaseOutlined,
  DollarOutlined,
  DownloadOutlined,
  FilterOutlined,
  PartitionOutlined,
  ReloadOutlined,
  SettingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation, useQuery } from '@apollo/client';
import {
  OPS_TOOL_SETTLEMENT_OPERATION_LOGS,
  OPS_TOOL_TMP_RUN_TASK_SUMMARY,
  OPS_TOOL_CLEAR_TMP_RUN_TASK,
  OPS_TOOL_INSERT_TMP_RUN_TASK,
  OPS_TOOL_RERUN_NEW_ZONE_BY_TMP_RUN_TASK,
  OPS_TOOL_RERUN_PRICING_BY_TMP_RUN_TASK,
  OPS_TOOL_RERUN_PRESORT_FEE_BY_TMP_RUN_TASK,
  OPS_TOOL_RESET_TMP_RUN_TASK_CONFIG_FOR_CLEARING,
  OPS_TOOL_RERUN_VOUCHER_CLEARING_BY_TMP_RUN_TASK,
} from '../graphql/opsToolSettlementGraphql';

type TmpRunTaskExecutionLogRow = {
  id: string;
  operatorId: string;
  operatorName: string;
  operatedAt: string | null;
  functionType: string;
  changeType: string;
  businessScenarioCode: string | null;
  operationDetail: string | null;
};

const TMP_RUN_TASK_RERUN_FUNCTION_TYPE = 'TMP_RUN_TASK_RERUN';

const nowrapHeader = () => ({ className: 'whitespace-nowrap text-center' });
const nowrapCell = () => ({ className: 'whitespace-nowrap text-center' });

const renderEllipsisCenter = (value: string | null | undefined) => {
  const text = value ?? '-';
  return (
    <Tooltip title={text}>
      <div className="truncate text-center">{text}</div>
    </Tooltip>
  );
};

const formatJsonDetail = (raw: string | null | undefined): string => {
  if (!raw) return '-';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

type StepKey =
  | 'clear'
  | 'insert'
  | 'newZone'
  | 'pricing'
  | 'presortFee'
  | 'resetConfig'
  | 'clearing';

type StepDefinition = {
  key: StepKey;
  title: string;
  description: string;
  icon: React.ReactNode;
  danger?: boolean;
  requiresRange?: boolean;
  requiresUploadFile?: boolean;
  requiresBusinessScenario?: boolean;
  independent?: boolean;
};

type UploadedCsvFile = {
  name: string;
  base64: string;
};

type TmpRunTaskRerunBusinessScenario = 'INCOME' | 'DSP';

type TmpRunTaskPricingRerunScenario = 'INCOME_TAIL' | 'INCOME_SORTING' | 'DSP';

const PRICING_SCENARIO_OPTIONS: { label: string; value: TmpRunTaskPricingRerunScenario }[] = [
  { label: '收入侧尾程段计费重跑', value: 'INCOME_TAIL' },
  { label: '收入侧分拣段计费重跑（TT分拣/取件/卸车费）', value: 'INCOME_SORTING' },
  { label: 'DSP侧计费重跑', value: 'DSP' },
];

const getPricingScenarioLabel = (scenario: TmpRunTaskPricingRerunScenario | null) =>
  scenario
    ? PRICING_SCENARIO_OPTIONS.find((item) => item.value === scenario)?.label ?? scenario
    : '未选择';

const BUSINESS_SCENARIO_OPTIONS: { label: string; value: TmpRunTaskRerunBusinessScenario }[] = [
  { label: '收入侧', value: 'INCOME' },
  { label: 'DSP侧', value: 'DSP' },
];

const getBusinessScenarioLabel = (scenario: TmpRunTaskRerunBusinessScenario | null) =>
  scenario
    ? BUSINESS_SCENARIO_OPTIONS.find((item) => item.value === scenario)?.label ?? scenario
    : '未选择';

const TMP_RUN_TASK_UPLOAD_MAX_HINT = '每次上传最多 70 万条数据';

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    key: 'clear',
    title: '清空 tmp_run_task',
    description: '逻辑删除当前表中全部未删除记录，为重跑准备干净环境。',
    icon: <ClearOutlined />,
    danger: true,
  },
  {
    key: 'insert',
    title: '插入待重跑订单',
    description: '上传 CSV 插入 business_id；单次上传上限见下方醒目提示。',
    icon: <DatabaseOutlined />,
    requiresUploadFile: true,
  },
  {
    key: 'newZone',
    title: '重跑 newZone',
    description: '按任务 ID 范围异步刷新订单 newZone 字段，可独立执行，不依赖插入订单。',
    icon: <PartitionOutlined />,
    requiresRange: true,
    independent: true,
    danger: true,
  },
  {
    key: 'pricing',
    title: '重跑计费',
    description:
      '按任务 ID 范围触发计费重算，需选择业务场景：收入侧尾程段 / 收入侧分拣段（TT分拣/取件/卸车费，场景 1001004）/ DSP 侧；尾程段与分拣段重跑互不影响。',
    icon: <DollarOutlined />,
    requiresRange: true,
    independent: true,
    danger: true,
    requiresBusinessScenario: true,
  },
  {
    key: 'presortFee',
    title: '重跑预分拣费',
    description: '按任务 ID 范围只重算并更新/插入预分拣费（TT预分拣费）一项，不影响该订单同节点下的其它费用项，可独立执行。',
    icon: <FilterOutlined />,
    requiresRange: true,
    independent: true,
    danger: true,
  },
  {
    key: 'resetConfig',
    title: '重置清分配置',
    description: '执行 tmp_run_task_config(id=2) 重置：page_num=1, is_stop=0。',
    icon: <SettingOutlined />,
    danger: true,
  },
  {
    key: 'clearing',
    title: '重跑清分',
    description: '基于 tmp_run_task 全量分页触发清分重跑，需选择收入侧或 DSP 侧业务场景；需先重置清分配置。',
    icon: <CloudSyncOutlined />,
    danger: true,
    independent: true,
    requiresBusinessScenario: true,
  },
];

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as ArrayBuffer);
      } else {
        reject(new Error('读取文件失败'));
      }
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsArrayBuffer(file);
  });

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const TmpRunTaskRerunTab: React.FC = () => {
  const [uploadedFile, setUploadedFile] = useState<UploadedCsvFile | null>(null);
  const [beginTaskId, setBeginTaskId] = useState<number | null>(null);
  const [endTaskId, setEndTaskId] = useState<number | null>(null);
  const [pricingBusinessScenario, setPricingBusinessScenario] =
    useState<TmpRunTaskPricingRerunScenario | null>(null);
  const [clearingBusinessScenario, setClearingBusinessScenario] =
    useState<TmpRunTaskRerunBusinessScenario | null>(null);
  const [clearExistingBeforeInsert, setClearExistingBeforeInsert] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(20);

  const [clearTmpRunTask, { loading: clearLoading }] = useMutation(OPS_TOOL_CLEAR_TMP_RUN_TASK);
  const [insertTmpRunTask, { loading: insertLoading }] = useMutation(OPS_TOOL_INSERT_TMP_RUN_TASK);
  const [rerunNewZone, { loading: newZoneLoading }] = useMutation(OPS_TOOL_RERUN_NEW_ZONE_BY_TMP_RUN_TASK);
  const [rerunPricing, { loading: pricingLoading }] = useMutation(OPS_TOOL_RERUN_PRICING_BY_TMP_RUN_TASK);
  const [rerunPresortFee, { loading: presortFeeLoading }] = useMutation(
    OPS_TOOL_RERUN_PRESORT_FEE_BY_TMP_RUN_TASK,
  );
  const [resetConfig, { loading: resetLoading }] = useMutation(OPS_TOOL_RESET_TMP_RUN_TASK_CONFIG_FOR_CLEARING);
  const [rerunClearing, { loading: clearingLoading }] = useMutation(OPS_TOOL_RERUN_VOUCHER_CLEARING_BY_TMP_RUN_TASK);

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(OPS_TOOL_TMP_RUN_TASK_SUMMARY, { fetchPolicy: 'network-only' });

  const activeTaskCount = summaryData?.opsToolTmpRunTaskSummary?.activeCount;

  const {
    data: executionLogData,
    loading: executionLogLoading,
    refetch: refetchExecutionLogs,
  } = useQuery(OPS_TOOL_SETTLEMENT_OPERATION_LOGS, {
    variables: {
      filters: {
        functionType: TMP_RUN_TASK_RERUN_FUNCTION_TYPE,
      },
      pageRequest: {
        page: logPage,
        size: logPageSize,
      },
    },
    fetchPolicy: 'network-only',
  });

  const executionLogPageResult = executionLogData?.opsToolSettlementOperationLogs;
  const executionLogRows: TmpRunTaskExecutionLogRow[] = useMemo(
    () =>
      (executionLogPageResult?.content ?? []).map((row: TmpRunTaskExecutionLogRow) => ({
        ...row,
        id: String(row.id),
        operatorId: String(row.operatorId),
        businessScenarioCode:
          row.businessScenarioCode != null ? String(row.businessScenarioCode) : null,
      })),
    [executionLogPageResult],
  );

  const refreshExecutionLogs = useCallback(async () => {
    setLogPage(1);
    await refetchExecutionLogs({
      filters: {
        functionType: TMP_RUN_TASK_RERUN_FUNCTION_TYPE,
      },
      pageRequest: {
        page: 1,
        size: logPageSize,
      },
    });
  }, [logPageSize, refetchExecutionLogs]);

  const executionLogColumns: ColumnsType<TmpRunTaskExecutionLogRow> = [
    {
      title: 'id',
      dataIndex: 'id',
      width: 80,
      fixed: 'left',
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
    },
    {
      title: 'operator_id',
      dataIndex: 'operatorId',
      width: 104,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'operator_name',
      dataIndex: 'operatorName',
      width: 120,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'operated_at',
      dataIndex: 'operatedAt',
      width: 176,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'function_type',
      dataIndex: 'functionType',
      width: 156,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'change_type',
      dataIndex: 'changeType',
      width: 248,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'business_scenario_code',
      dataIndex: 'businessScenarioCode',
      width: 168,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: renderEllipsisCenter,
    },
    {
      title: 'operation_detail',
      dataIndex: 'operationDetail',
      width: 280,
      align: 'center',
      onHeaderCell: nowrapHeader,
      onCell: nowrapCell,
      render: (value: string | null) => {
        if (!value) {
          return '-';
        }
        const display = value.replace(/\s+/g, ' ');
        const formatted = formatJsonDetail(value);
        return (
          <Tooltip
            title={
              <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap text-left text-xs leading-5">
                {formatted}
              </pre>
            }
            overlayInnerStyle={{ maxWidth: 640 }}
          >
            <div className="truncate text-center">{display}</div>
          </Tooltip>
        );
      },
    },
  ];

  const loadingByKey: Record<StepKey, boolean> = {
    clear: clearLoading,
    insert: insertLoading,
    newZone: newZoneLoading,
    pricing: pricingLoading,
    presortFee: presortFeeLoading,
    resetConfig: resetLoading,
    clearing: clearingLoading,
  };

  const validateRange = () => {
    if (beginTaskId == null || endTaskId == null) {
      message.error('请先输入 beginTaskId / endTaskId');
      return false;
    }
    if (beginTaskId > endTaskId) {
      message.error('beginTaskId 不能大于 endTaskId');
      return false;
    }
    return true;
  };

  const handleAutoFillTaskIdRange = async () => {
    const { data } = await refetchSummary();
    const summary = data?.opsToolTmpRunTaskSummary;
    const minActiveId = summary?.minActiveId;
    const maxActiveId = summary?.maxActiveId;
    if (minActiveId == null || maxActiveId == null) {
      message.warning('当前无有效任务记录，无法自动填充');
      return;
    }
    setBeginTaskId(Number(minActiveId));
    setEndTaskId(Number(maxActiveId));
    message.success(`已填充任务 ID 范围：${minActiveId} ~ ${maxActiveId}`);
  };

  const withConfirm = (
    title: string,
    content: string,
    onOk: () => Promise<void>,
    danger?: boolean,
  ) => {
    Modal.confirm({
      title,
      content,
      okText: '确认执行',
      cancelText: '取消',
      okButtonProps: danger ? { danger: true } : undefined,
      onOk,
    });
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'business_id\n';
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'tmp_run_task_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('模板下载成功');
  };

  const handleUploadFile = async (file: UploadFile) => {
    const rawFile = file as unknown as File;
    const isCSV = file.type === 'text/csv' || file.name?.toLowerCase().endsWith('.csv');
    if (!isCSV) {
      message.error('只能上传 CSV 格式文件');
      return Upload.LIST_IGNORE;
    }
    try {
      const buffer = await readFileAsArrayBuffer(rawFile);
      const base64 = arrayBufferToBase64(buffer);
      setUploadedFile({ name: file.name, base64 });
      message.success(`已选择文件：${file.name}`);
    } catch (error: unknown) {
      const errorText = error instanceof Error ? error.message : '读取文件失败';
      message.error(errorText);
    }
    return false;
  };

  const handleClear = () =>
    withConfirm(
      '确认清空 tmp_run_task',
      '该操作会将当前 tmp_run_task 中未删除数据全部逻辑删除，不可自动恢复。',
      async () => {
        const { data } = await clearTmpRunTask();
        const result = data?.opsToolClearTmpRunTask;
        if (result?.success) {
          message.success(result.message);
          await refreshExecutionLogs();
          await refetchSummary();
          return;
        }
        const errorText = result?.message ?? '清空失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );

  const handleInsert = () =>
    withConfirm(
      '确认插入待重跑订单',
      [
        `将解析文件「${uploadedFile?.name ?? ''}」并插入 business_id 到 tmp_run_task。`,
        clearExistingBeforeInsert
          ? '已选择插入前清空表中未删除记录。'
          : '将在现有数据后追加，不会清空已有记录。',
      ].join(''),
      async () => {
        if (!uploadedFile) {
          message.error('请先上传 CSV 文件');
          return;
        }
        const { data } = await insertTmpRunTask({
          variables: {
            input: {
              fileContentBase64: uploadedFile.base64,
              fileName: uploadedFile.name,
              clearExistingBeforeInsert,
            },
          },
        });
        const result = data?.opsToolInsertTmpRunTask;
        if (result?.success) {
          message.success(result.message || '正在插入中，请稍后验证条数是否正确');
          await refreshExecutionLogs();
          await refetchSummary();
          return;
        }
        const errorText = result?.message ?? '插入失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
    );

  const handleRerunNewZone = () =>
    withConfirm(
      '确认重跑 newZone',
      `将按照任务id范围 ${beginTaskId} ~ ${endTaskId} 执行 newZone 重跑。`,
      async () => {
        if (!validateRange()) {
          return;
        }
        const { data } = await rerunNewZone({
          variables: { input: { beginTaskId, endTaskId } },
        });
        const result = data?.opsToolRerunNewZoneByTmpRunTask;
        if (result?.success) {
          const text = `${result.message}，提交运单数 ${result.affectedCount ?? 0}`;
          message.success(text);
          await refreshExecutionLogs();
          return;
        }
        const errorText = result?.message ?? '重跑 newZone 失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );

  const handleRerunPricing = () => {
    if (!pricingBusinessScenario) {
      message.error('请先选择计费重跑业务场景');
      return;
    }
    withConfirm(
      '确认重跑计费',
      `将按照任务id范围 ${beginTaskId} ~ ${endTaskId} 执行【${getPricingScenarioLabel(pricingBusinessScenario)}】。`,
      async () => {
        if (!validateRange()) {
          return;
        }
        if (!pricingBusinessScenario) {
          message.error('请先选择计费重跑业务场景');
          return;
        }
        const { data } = await rerunPricing({
          variables: {
            input: {
              beginTaskId,
              endTaskId,
              businessScenario: pricingBusinessScenario,
            },
          },
        });
        const result = data?.opsToolRerunPricingByTmpRunTask;
        if (result?.success) {
          const text = `${result.message}，任务ID: ${result.taskId ?? '-'}`;
          message.success(text);
          await refreshExecutionLogs();
          return;
        }
        const errorText = result?.message ?? '重跑计费失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );
  };

  const handleRerunPresortFee = () =>
    withConfirm(
      '确认重跑预分拣费',
      `将按照任务id范围 ${beginTaskId} ~ ${endTaskId} 只重算并更新/插入预分拣费一项，不影响其它费用项。`,
      async () => {
        if (!validateRange()) {
          return;
        }
        const { data } = await rerunPresortFee({
          variables: { input: { beginTaskId, endTaskId } },
        });
        const result = data?.opsToolRerunPresortFeeByTmpRunTask;
        if (result?.success) {
          const text = `${result.message}，任务ID: ${result.taskId ?? '-'}`;
          message.success(text);
          await refreshExecutionLogs();
          return;
        }
        const errorText = result?.message ?? '重跑预分拣费失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );

  const handleResetConfig = () =>
    withConfirm(
      '确认重置清分配置',
      '将执行 tmp_run_task_config(id=2): page_num=1, is_stop=0。',
      async () => {
        const { data } = await resetConfig();
        const result = data?.opsToolResetTmpRunTaskConfigForClearing;
        if (result?.success) {
          message.success(result.message);
          await refreshExecutionLogs();
          return;
        }
        const errorText = result?.message ?? '重置清分配置失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );

  const handleRerunClearing = () => {
    if (!clearingBusinessScenario) {
      message.error('请先选择清分重跑业务场景');
      return;
    }
    withConfirm(
      '确认重跑清分',
      `将基于 tmp_run_task 全量分页触发${getBusinessScenarioLabel(clearingBusinessScenario)}清分重跑。`,
      async () => {
        if (!clearingBusinessScenario) {
          message.error('请先选择清分重跑业务场景');
          return;
        }
        const { data } = await rerunClearing({
          variables: {
            input: {
              businessScenario: clearingBusinessScenario,
            },
          },
        });
        const result = data?.opsToolRerunVoucherClearingByTmpRunTask;
        if (result?.success) {
          const text = `${result.message}，runTaskId: ${result.taskId ?? '-'}`;
          message.success(text);
          await refreshExecutionLogs();
          return;
        }
        const errorText = result?.message ?? '重跑清分失败';
        message.error(errorText);
        await refreshExecutionLogs();
      },
      true,
    );
  };

  const handlers: Record<StepKey, () => void> = {
    clear: handleClear,
    insert: handleInsert,
    newZone: handleRerunNewZone,
    pricing: handleRerunPricing,
    presortFee: handleRerunPresortFee,
    resetConfig: handleResetConfig,
    clearing: handleRerunClearing,
  };

  const isStepDisabled = (step: StepDefinition) => {
    if (step.requiresUploadFile && !uploadedFile) {
      return true;
    }
    if (step.requiresRange && (beginTaskId == null || endTaskId == null)) {
      return true;
    }
    if (step.key === 'pricing' && !pricingBusinessScenario) {
      return true;
    }
    if (step.key === 'clearing' && !clearingBusinessScenario) {
      return true;
    }
    return false;
  };

  return (
    <div className="space-y-4">
      <Alert
        type="warning"
        showIcon
        message="高风险运维操作"
        description="Step1 / Step2 用于准备 tmp_run_task 数据。重跑 newZone、重跑计费、重跑预分拣费、重跑清分四者互不依赖、可任意顺序执行，且不依赖 Step2。重跑计费需选择业务场景（收入侧尾程段 / 收入侧分拣段 / DSP侧），尾程段与分拣段重跑互不影响，分拣段只处理TT分拣/取件/卸车费；重跑清分需选择收入侧 / DSP侧；重跑预分拣费只更新/插入预分拣费一项，不影响该订单其它费用项。"
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card title="输入参数" size="small" className="h-full">
            <Form layout="vertical">
              <Form.Item
                label="待重跑订单文件"
                required
                extra="Step2 使用，CSV 首行表头为 business_id，仅插入数据行运单号"
              >
                <Space direction="vertical" className="w-full" size="middle">
                  <Alert
                    type="warning"
                    showIcon
                    banner
                    message={
                      <Typography.Text strong className="!text-[15px]">
                        {TMP_RUN_TASK_UPLOAD_MAX_HINT}
                      </Typography.Text>
                    }
                    description="超过上限时服务端将拒绝导入，请拆分为多个 CSV 分批上传。"
                  />
                  <Space wrap>
                    <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                      下载模板
                    </Button>
                    <Upload
                      accept=".csv"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={handleUploadFile}
                    >
                      <Button icon={<UploadOutlined />}>上传 CSV</Button>
                    </Upload>
                  </Space>
                  <Checkbox
                    checked={clearExistingBeforeInsert}
                    onChange={(event) => setClearExistingBeforeInsert(event.target.checked)}
                  >
                    插入前清空表中未删除记录
                  </Checkbox>
                  {uploadedFile ? (
                    <Space>
                      <Tag color="blue">{uploadedFile.name}</Tag>
                      <Button type="link" size="small" onClick={() => setUploadedFile(null)}>
                        清除
                      </Button>
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">尚未上传文件</Typography.Text>
                  )}
                </Space>
              </Form.Item>
              <Divider className="my-3" />
              <Form.Item
                className="[&_.ant-form-item-label>label]:!w-full"
                label={
                  <div className="flex w-full items-center justify-between gap-2">
                    <span>
                      <span className="text-red-500">*</span>
                      <span className="ml-1">任务 ID 范围</span>
                    </span>
                    <Button
                      type="link"
                      size="small"
                      className="!h-auto !p-0"
                      loading={summaryLoading}
                      onClick={handleAutoFillTaskIdRange}
                    >
                      自动填充全量有效的任务ID
                    </Button>
                  </div>
                }
                extra="重跑 newZone / 计费 使用，手动填写 tmp_run_task 的 id 范围"
              >
                <Space wrap>
                  <InputNumber
                    value={beginTaskId}
                    onChange={setBeginTaskId}
                    min={1}
                    placeholder="beginTaskId"
                    className="w-36"
                  />
                  <Typography.Text type="secondary">~</Typography.Text>
                  <InputNumber
                    value={endTaskId}
                    onChange={setEndTaskId}
                    min={1}
                    placeholder="endTaskId"
                    className="w-36"
                  />
                </Space>
              </Form.Item>
              <Divider className="my-3" />
              <Form.Item label="计费重跑业务场景" required extra="Step4 使用">
                <Select
                  value={pricingBusinessScenario ?? undefined}
                  onChange={(value) => setPricingBusinessScenario(value ?? null)}
                  options={PRICING_SCENARIO_OPTIONS}
                  placeholder="请选择业务场景"
                  allowClear
                  className="w-full"
                />
              </Form.Item>
              <Form.Item label="清分重跑业务场景" required extra="Step7 使用">
                <Select
                  value={clearingBusinessScenario ?? undefined}
                  onChange={(value) => setClearingBusinessScenario(value ?? null)}
                  options={BUSINESS_SCENARIO_OPTIONS}
                  placeholder="请选择业务场景"
                  allowClear
                  className="w-full"
                />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card title="分步执行" size="small">
            <List
              dataSource={STEP_DEFINITIONS}
              renderItem={(step, index) => {
                const disabled = isStepDisabled(step);
                const stepDescription =
                  step.key === 'clear' ? (
                    <Space direction="vertical" size={4}>
                      <Typography.Text type="secondary">{step.description}</Typography.Text>
                      <Space>
                        <Typography.Text>
                          当前tmp_run_task表共有记录：
                          <Typography.Text strong>
                            {summaryLoading ? '加载中...' : (activeTaskCount ?? '-')}
                          </Typography.Text>
                          {' '}条
                        </Typography.Text>
                        <Button type="link" size="small" loading={summaryLoading} onClick={() => refetchSummary()}>
                          刷新
                        </Button>
                      </Space>
                    </Space>
                  ) : step.key === 'insert' ? (
                    <Space direction="vertical" size={4}>
                      <Alert
                        type="warning"
                        showIcon
                        className="!py-1"
                        message={<Typography.Text strong>{TMP_RUN_TASK_UPLOAD_MAX_HINT}</Typography.Text>}
                      />
                      <Typography.Text type="secondary">{step.description}</Typography.Text>
                      <Typography.Text>
                        已选文件：
                        <Typography.Text strong>{uploadedFile?.name ?? '未上传'}</Typography.Text>
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        插入前清空：
                        <Typography.Text strong>{clearExistingBeforeInsert ? '是' : '否（追加）'}</Typography.Text>
                      </Typography.Text>
                    </Space>
                  ) : step.key === 'pricing' ? (
                    <Space direction="vertical" size={4}>
                      <Typography.Text type="secondary">{step.description}</Typography.Text>
                      <Typography.Text>
                        当前业务场景：
                        <Typography.Text strong type={pricingBusinessScenario ? undefined : 'danger'}>
                          {getPricingScenarioLabel(pricingBusinessScenario)}
                        </Typography.Text>
                      </Typography.Text>
                    </Space>
                  ) : step.key === 'clearing' ? (
                    <Space direction="vertical" size={4}>
                      <Typography.Text type="secondary">{step.description}</Typography.Text>
                      <Typography.Text>
                        当前业务场景：
                        <Typography.Text strong type={clearingBusinessScenario ? undefined : 'danger'}>
                          {getBusinessScenarioLabel(clearingBusinessScenario)}
                        </Typography.Text>
                      </Typography.Text>
                    </Space>
                  ) : (
                    step.description
                  );
                return (
                  <List.Item
                    className="!px-0"
                    actions={[
                      <Button
                        key="run"
                        type={step.danger ? 'default' : 'primary'}
                        danger={step.danger}
                        loading={loadingByKey[step.key]}
                        disabled={disabled}
                        onClick={handlers[step.key]}
                      >
                        执行
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                          {step.icon}
                        </div>
                      }
                      title={
                        <Space>
                          <Tag>Step{index + 1}</Tag>
                          <span>{step.title}</span>
                          {step.key === 'clear' && activeTaskCount != null && (
                            <Tag color={activeTaskCount > 0 ? 'orange' : 'default'}>
                              当前 {activeTaskCount} 条
                            </Tag>
                          )}
                          {step.danger && <Tag color="red">高风险</Tag>}
                          {step.independent && <Tag color="green">可独立执行</Tag>}
                          {step.requiresRange && <Tag color="gold">需任务范围</Tag>}
                          {step.requiresBusinessScenario && <Tag color="purple">需业务场景</Tag>}
                          {step.requiresUploadFile && <Tag color="cyan">需上传文件</Tag>}
                        </Space>
                      }
                      description={stepDescription}
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="执行日志"
        size="small"
        extra={
          <Button
            type="link"
            icon={<ReloadOutlined />}
            loading={executionLogLoading}
            onClick={() => refetchExecutionLogs()}
          >
            刷新
          </Button>
        }
      >
        <Table<TmpRunTaskExecutionLogRow>
          rowKey="id"
          size="small"
          loading={executionLogLoading}
          columns={executionLogColumns}
          dataSource={executionLogRows}
          tableLayout="fixed"
          scroll={{ x: 1400 }}
          pagination={{
            current: logPage,
            pageSize: logPageSize,
            total: executionLogPageResult?.totalElements ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => {
              setLogPage(page);
              setLogPageSize(size);
            },
          }}
          locale={{
            emptyText: '暂无执行记录',
          }}
        />
      </Card>
    </div>
  );
};

export default TmpRunTaskRerunTab;
